import { useEffect, useState } from "react";
import { Icon, IconButton, Input, Select, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Tag } from "../lib/api.js";
import "./tag-picker.css";

/** The office's tags, for pickers and filters. Reloaded whenever `refreshKey` changes. */
export function useTags(
  enabled = true,
  refreshKey = 0,
): [Tag[], (tag: Tag) => void] {
  const [tags, setTags] = useState<Tag[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .get<{ tags: Tag[] }>("/tags?limit=200")
      .then((res) => !cancelled && setTags(res.tags))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshKey]);
  return [
    tags,
    (tag) =>
      setTags((prev) =>
        prev.some((t) => t.id === tag.id) ? prev : [...prev, tag],
      ),
  ];
}

/** A tag as a coloured chip. Small enough to sit inline beside a task title. */
export function TagChip({
  tag,
  onRemove,
}: {
  tag: Pick<Tag, "id" | "name" | "color">;
  onRemove?: () => void;
}) {
  return (
    <span className="tag-chip" style={{ ["--tag" as string]: tag.color }}>
      <span className="tag-chip__name">{tag.name}</span>
      {onRemove && (
        <IconButton
          icon={<Icon name="close" size={11} />}
          label={`Remove tag ${tag.name}`}
          size="sm"
          onClick={onRemove}
        />
      )}
    </span>
  );
}

interface Props {
  /** The tags currently on the task. */
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  disabled?: boolean;
  /** Offer creating a tag by name when none fits. */
  canCreate?: boolean;
}

/** Choose from the office's tags, or make a new one by typing its name. */
export function TagPicker({
  value,
  onChange,
  disabled = false,
  canCreate = true,
}: Props) {
  const { toast } = useToast();
  const [all, remember] = useTags();
  const [draft, setDraft] = useState("");
  const [color, setColor] = useState("#64748b");
  const chosen = new Set(value.map((t) => t.id));

  const create = () => {
    const name = draft.trim();
    if (!name) return;
    // An existing tag of that name is picked rather than duplicated.
    const existing = all.find(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) {
      if (!chosen.has(existing.id)) onChange([...value, existing]);
      setDraft("");
      return;
    }
    api
      .post<{ tag: Tag }>("/tags", { name, color })
      .then((res) => {
        remember(res.tag);
        onChange([...value, res.tag]);
        setDraft("");
      })
      .catch((e: unknown) =>
        toast(
          e instanceof ApiError ? e.message : "Could not create that tag.",
          { tone: "danger" },
        ),
      );
  };

  return (
    <div className="tag-picker">
      <div className="tag-picker__chips">
        {value.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            onRemove={
              disabled
                ? undefined
                : () => onChange(value.filter((t) => t.id !== tag.id))
            }
          />
        ))}
        {value.length === 0 && (
          <span className="tag-picker__empty">No tags</span>
        )}
      </div>
      {!disabled && (
        <div className="tag-picker__add">
          <Select
            aria-label="Add a tag"
            value=""
            onChange={(e) => {
              const tag = all.find((t) => t.id === e.target.value);
              if (tag) onChange([...value, tag]);
            }}
          >
            <option value="">Add a tag…</option>
            {all
              .filter((t) => !chosen.has(t.id))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </Select>
          {canCreate && (
            <div className="tag-picker__new">
              <input
                type="color"
                aria-label="Colour for a new tag"
                className="tag-picker__color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
              <Input
                aria-label="New tag name"
                placeholder="New tag…"
                value={draft}
                maxLength={40}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    create();
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
